<script setup>
import ShowMorePanel from "~/components/ShowMorePanel.vue";
import { useUtilityStore } from "~/store/UtililityStore";
import { useCartStore } from "~/store/CartStore";
import { useProductStore } from "~/store/ProductStore";
import { useAttributeStore } from "~/store/AttributeStore";
import { useUserStore } from "~/store/UserStore";

import {
  trackViewItem,
  trackAddToCart,
  mapViewProduct,
} from "~/composables/gtmEvents";
import { useFilterStore } from "~/store/FilterStore";
import { useI18n } from "vue-i18n";


const utilityStore = useUtilityStore();
const cartStore = useCartStore();
const userStore = useUserStore();
const productStore = useProductStore();


const attributeStore = useAttributeStore();
const localePath = useLocalePath();
const tour = useTour();
const router = useRouter();
const backOrders = computed(() => productStore.backOrders);
const associationsProducts = computed(() => productStore.associationsProducts);
const recentlyViewed = computed(() => productStore.recentlyViewed);
// useGtm is auto-imported by @saslavik/nuxt-gtm module
const gtm = useGtm();
const isAuthenticated = computed(() => userStore.isAuthenticated);
const cldUser = computed(() => userStore.cldUser);
const selectedLanguage = computed(() => utilityStore.selectedLanguage);
// const selectedLocale = computed(() => utilityStore.selectedLocale);
// const product = computed(() => productStore.product);
const attributes = computed(() => attributeStore.attributes);
const attributesDetails = computed(() => attributeStore.attributesDetails);

const attributesMain = computed(() => Object.values(attributeStore.attributesMain || {}));
const attributesMainCatalogText = computed(() => attributeStore.attributesMainCatalogText);

const attributeOptionLables = computed(
  () => attributeStore.attributeOptionLables
);

const priceAttributes = ref([]);

// Age-rating attributes (PEGI / USK / ESRB) render as official-style badges.
const isRatingAttr = (attGroup) => /pegi|usk|esrb/i.test(attGroup?.options?.en_GB || '');
// Brand attribute row links to the product's publisher page (same target as
// the header link). Detected by the en_GB option label, like isRatingAttr.
const isBrandAttr = (attGroup) => /^brand$/i.test(attGroup?.options?.en_GB || '');
const isPegiDescriptors = (attGroup) =>
  attGroup?.data?.[0]?.Attribute === 'vg_pegi_descriptors' || /pegi/i.test(attGroup?.options?.en_GB || '');
const selectedQuantity = ref(1);
const preSalePercentage = computed(() => productStore.preSalePercentage);

const isLoading = ref(false);
const isDownloading = ref(false);
const route = useRoute();
const filterStore = useFilterStore();
const { id, name } = route.params;
const slideId = ref(0)
const { data: product, error, status } = await useAsyncData(`Product:${id}`, () => {
  return productStore.fetchProduct({
    id: id,
    selectedLanguage: selectedLanguage.value,
  })
}, {
  immediate: true,
  lazy: false,
  watch: [selectedLanguage],
})

if (!product.value) {
  // Set HTTP status on server, but DON'T throw
  if (import.meta.server) {
    setResponseStatus(410)
  }
  useHead({
    title: 'Product not available',
    meta: [{ name: 'robots', content: 'noindex, follow' }],
  })
}
// const product = computed(() => productStore.product);

// Publisher-page link shown in the product header: resolves the product's
// brand to a CMS publisher entry. The search brand facet (and the CMS
// brandFilterValue) is the en_GB brand text, so lookup + slug use that; the
// localized text is only for display.
const brandFacetValue = computed(() => {
  const bt = product.value?.brand_text;
  return typeof bt === "string" ? bt : bt?.en_GB || "";
});
const brandDisplayName = computed(() => {
  const bt = product.value?.brand_text;
  if (typeof bt === "string") return bt;
  return bt?.[selectedLanguage.value] || bt?.en_GB || "";
});
const brandPublisherKey = computed(() => `brand-publisher|${brandFacetValue.value}`);
const { data: brandPublisher } = useAsyncData(
  brandPublisherKey,
  async () => {
    if (!brandFacetValue.value) return null;
    try {
      return await $fetch(
        `/api/cms/cld-publisher-by-brand?brand=${encodeURIComponent(brandFacetValue.value)}`
      );
    } catch {
      return null;
    }
  },
  { default: () => null },
);

// The link works for every branded product: CMS entry slug when one exists,
// otherwise the slugified brand (served as a virtual publisher page).
const publisherPageLink = computed(() => {
  if (brandPublisher.value) {
    return {
      href: localePath(`/publisher/${brandPublisher.value.slug}`),
      name: brandPublisher.value.name,
    };
  }
  const brandSlug = brandFacetValue.value ? slugifyBrand(brandFacetValue.value) : "";
  return brandSlug
    ? { href: localePath(`/publisher/${brandSlug}`), name: brandDisplayName.value || brandFacetValue.value }
    : null;
});

// License-page link, next to the publisher one. The detail payload carries no
// license field, so the code (e.g. "harry_potter") comes from a one-shot
// multi-id search; it is then resolved to a CMS license entry, falling back to
// the virtual page under the slugified code.
const { data: productLicenseCode } = useAsyncData(
  `product-license|${id}`,
  async () => {
    try {
      const data = await $fetch(useURLs[NODE_ENV].PRODUCT_SEARCH_URL, {
        method: "POST",
        body: {
          prefix: "",
          range: 1,
          startRowIndex: 1,
          filters: { attribute: {}, categories: [], release: {} },
          selectedLanguage: selectedLanguage.value || "en_GB",
          sortOrder: "",
          searchByMultiIds: { active: true, ids: [String(id)] },
        },
        headers: {
          client_id: useURLs[NODE_ENV].CLIENT_ID,
          client_secret: useURLs[NODE_ENV].CLIENT_SECERET,
          "Content-Type": "application/json",
        },
        retry: 1,
      });
      const lic = String(data?.products?.[0]?.license || "").trim();
      return lic && lic.toLowerCase() !== "none" ? lic : null;
    } catch {
      return null;
    }
  },
  { default: () => null },
);

const licenseRefKey = computed(() => `license-ref|${productLicenseCode.value ?? ""}`);
const { data: licenseRef } = useAsyncData(
  licenseRefKey,
  async () => {
    if (!productLicenseCode.value) return null;
    try {
      return await $fetch(
        `/api/cms/cld-license-by-value?value=${encodeURIComponent(productLicenseCode.value)}`
      );
    } catch {
      return null;
    }
  },
  { default: () => null, watch: [productLicenseCode] },
);

const licensePageLink = computed(() => {
  if (licenseRef.value) {
    return {
      href: localePath(`/license/${licenseRef.value.slug}`),
      name: licenseRef.value.name,
    };
  }
  const code = productLicenseCode.value;
  const licSlug = code ? slugifyBrand(code) : "";
  return code && licSlug
    ? { href: localePath(`/license/${licSlug}`), name: prettyLicenseName(code) }
    : null;
});

const getImgUrl = (img, h = 200) =>
  `https://assets.cld.eu/cdn-cgi/image/format=auto,height=${h},quality=80/${img}`;


const { t, locale } = useI18n();
const { showDecimalValueInLocale, productLinkTitleString, } = useUtils();

const fbUrl = ref("");

const addToCart = (e) => {
  e.preventDefault();
  addToCartCldWebApi({
    sku: product.value.identifier,
    qty: selectedQuantity.value,
  });
};

const geBackOrders = async () => {
  if (!id) return;
  setTimeout(() => {
    productStore.getBackOrdersCldWebApi({ skus: [id] });
  }, 100);
};
/*

const updateProductBreadCrumb = (id) => {

  if (product.value?.identifier !== id) {
    return;
  }
  // filterStore.setProductInfoBreadCrumbs({ dataArray: [], id })
  filterStore.setBreadCrumbs({
    dataArray: [],
    name: "products-id-name",
    index: 0,
  });
  const _breadcrumb = [
    {
      text: "Home",
      index: 0,
      name: "products-id-name",
      path: "/",
    },
    {
      text: "products-search",
      name: "products-search",
      path: "/products/search",
    },
  ];

  const Clabels = {};
  if (product.value.category_tree?.length) {
    Clabels["1"] =
      product.value.category_tree[0].Labels[selectedLanguage.value];
    _breadcrumb.push({
      text: Clabels["1"],
      index: 0,
      name: "products-id-name",
      path: null,
    });
  }
  if (
    product.value.category_tree?.length &&
    product.value.category_tree[0]?.Childrens?.length
  ) {
    Clabels["2"] =
      product.value.category_tree[0]?.Childrens[0]?.Labels[
      selectedLanguage.value
      ];
    _breadcrumb.push({
      text: Clabels["2"],
      path: null,
    });
  }
  if (
    product.value.category_tree?.length &&
    product.value.category_tree[0]?.Childrens?.length &&
    product.value.category_tree[0]?.Childrens[0]?.Childrens?.length
  ) {
    Clabels["3"] =
      product.value.category_tree[0].Childrens[0].Childrens[0].Labels[
      selectedLanguage.value
      ];
    _breadcrumb.push({
      text: Clabels["3"],
      path: null,
    });
  }
  _breadcrumb.push({
    text: product.value.name,
    path:
      "/products/" +
      product.value.identifier +
      "/" +
      (product.value.name)
        .replaceAll(/[^a-zA-Z0-9]+/g, "-")
        .toLowerCase(),
  });
  filterStore.setBreadCrumbs({
    dataArray: _breadcrumb,
    name: "products-id-name",
    index: 0,
  });
  // filterStore.setProductInfoBreadCrumbs({ dataArray: _breadcrumb })
}; */
const getProduct = async (_id) => {
  // setTimeout(async (_id) => {
  productStore
    .fetchProduct({
      id: id,
      selectedLanguage: selectedLanguage.value,
    })
    .then(() => {
      setTimeout(() => {

        //updateProductBreadCrumb(id);
        gtmViewProduct();
      }, 100);

    })

  // }, 1000);

  //
  //
};

const addToCartCldWebApi = async ({ sku, qty }) => {
  isLoading.value = true;
  try {
    const { status } = await cartStore.addToCart({ sku, qty });
    if (status) {
      useNuxtApp().$showToast({
        msg: t("alerts.added_to_cart.desc"),
        type: "success",
      });
      gtmAddToCart();
    } else {
      useNuxtApp().$showToast({
        msg: t("alerts.failed_add_cart.desc"),
        type: "error",
      });
    }
  } finally {
    isLoading.value = false;
  }
};
const getProductPreSalePercentage = async () => {
  if (!id) {
    return;
  }
  await productStore.getProductPreSalePercentageCld(id);
};

const getTitleMulti = computed(
  () => (values) =>
    values
      .map(
        (v) => {
          try {
            return (attributeOptionLables.value &&
              attributeOptionLables.value["platforms"] &&
              attributeOptionLables.value["platforms"]["lables"][v][
              selectedLanguage.value
              ]) ||
              (attributeOptionLables.value &&
                attributeOptionLables.value["platforms"] &&
                attributeOptionLables.value["platforms"]["lables"][v]["en_GB"]) ||
              v
          } catch (error) {
            return v.replace(/_/g, ' ')
              .replace(/\b\w/g, c => c.toUpperCase());
          }
        }

      )
      .join(", ")
);
const getText = computed(
  () => (data) =>
    data && Object.prototype.hasOwnProperty.call(data, selectedLanguage.value)
      ? data[selectedLanguage.value]
      : data && data["en_GB"]
        ? data["en_GB"]
        : ""
);
// const showDecimalValueInLocale = computed(() => {
//   return (v) => {
//     if (v) {
//       try {
//         return Number(v).toFixed(2).toLocaleString(selectedLocale);
//       } catch (error) {
//         return v;
//       }
//     }
//     return "";
//   };
// });
// const getTitle = computed(
//   () => (key, value) =>
//     (attributeOptionLables.value &&
//       attributeOptionLables.value[key]["lables"][value][
//       selectedLanguage.value
//       ]) ||
//     (attributeOptionLables.value &&
//       attributeOptionLables.value[key]["lables"][value]["en_GB"]) ||
//     value
// );
const MAX_QUANTITY = 10000;
const increment = () => {
  selectedQuantity.value = Math.min(Number(selectedQuantity.value) + 1, MAX_QUANTITY);
};
const decrement = () => {
  if (Number(selectedQuantity.value) > 1) {
    selectedQuantity.value = Number(selectedQuantity.value) - 1;
  }
};
const onQuantityInput = (event) => {
  const digits = event.target.value.replace(/\D/g, "");
  if (!digits) {
    selectedQuantity.value = "";
  } else {
    selectedQuantity.value = Math.min(Math.max(parseInt(digits, 10), 1), MAX_QUANTITY);
  }
  event.target.value = selectedQuantity.value;
};
const onQuantityBlur = () => {
  if (!selectedQuantity.value || Number(selectedQuantity.value) < 1) {
    selectedQuantity.value = 1;
  }
};
const gtmViewProduct = () => {
  // console.log("cldUser",cldUser.value);
  setTimeout(() => {
    try {
      const mappedItem = mapViewProduct([product.value]);
      trackViewItem({
        items: mappedItem,
        value: product.value["dvdSuggestedPriceAmount"],
        customer_id: cldUser.value?.id,
      });
    } catch (error) {
      // console.log("gtm error", error);
    }
  }, 3000);
};
const gtmAddToCart = () => {
  const mappedItem = mapViewProduct([product.value]);
  trackAddToCart({
    items: mappedItem,
    customer_id: cldUser.value.id,
    value: +product.value["dvdSuggestedPriceAmount"] * selectedQuantity.value,
  });
};
const downloadHighResImages = async () => {

  if (!isAuthenticated.value) {
    return;
  }
  isDownloading.value = true;
  const imgId = `${product.value.identifier}_${product.value.name
    }`
  await utilityStore.downloadImages(
    product.value.images,
    imgId
  );
  isDownloading.value = false;
};

// watch(computedLocale, () => {
//   console.error("Watch computedLocale", computedLocale.value);
//   loading.value = !loading.value;
//   setTimeout(() => {
//     loading.value = !loading.value;
//   }, 0);
// }, { immediate: false, deep: false });

// const { data: Attributes } = await useAsyncData(`Attributes:${id}`, () => attributeStore.getAttributes())
useAsyncData(`Attributes:${id}-${selectedLanguage.value}`, () => {
  return attributeStore.findAttributesById(id)

}, {
  lazy: true,
  server: false,
  watch: [selectedLanguage],
  immediate: true,

});
useAsyncData(`AddAttributes:${id}`, () => addPriceInAttributes(), {
  lazy: true,
  server: false,
  immediate: false
})
useAsyncData(`Viewed-Products-${id}`, () => { productStore.fetchViewedProducts({ selectedLanguage: selectedLanguage.value, id }); return true; }, {
  server: false,
  lazy: true
})
onMounted(() => {

  geBackOrders();
  getProductPreSalePercentage();
  addPriceInAttributes();

});
onMounted(() => {
  if (process.client) {
    productStore.addViewedItem(id).then().catch()
    setTimeout(() => {
      try {
        fbUrl.value = `https://www.facebook.com/sharer/sharer.php?u=${window.location.host
          }&t=${product.value.name}`;
        attributeStore.findAttributesById(id)
      } catch (error) { }
    }, 2000);
  }
})
const showTour = () => {
  tour.init({ idX: "tour-product-info" });
  const tourButtons = tour.useButtons();
  tour.addSteps([
    {
      attachTo: { element: "#download-images-btn", on: "bottom" },
      text: t("tour.download_images_button"),
      buttons: [tourButtons.cancel, tourButtons.nextWithUpdate],
    },
    {
      // attachTo: { element: "", on: "bottom" },
      text: t("tour.restart_tutorial_icon"),
      buttons: [
        {
          action: function () {
            tour.restart([tour.ids.value.search_page, tour.ids.value.product_page]);
            return router.push({ path: localePath("/products/search") });
          },
          text: t("common_button.restart"),
          classes: "danger-tour-btn"
        },
        tourButtons.cancel,

      ],
    },
  ]);
  tour.startTour();
};
onMounted(() => {

  setTimeout(() => {
    // showTour();
  }, 1000);
})
onBeforeUnmount(() => {
  tour.cancel(false);
})

const addPriceInAttributes = () => {
  // setTimeout(() => {
  if (priceAttributes.value.some(
    (attr) => attr.type === "pim_catalog_price_collection" && attr.data === product.value.price
  )) {
    return true;
  }
  if (product.value?.price) {
    priceAttributes.value.push({
      type: "pim_catalog_price_collection",
      data: product.value.price,
      options: {
        i18nKey: "price",
      },
      groupLables: {
        en_GB: "Pricing",
        sv_SV: "Prissättning",
        it_IT: "Prezzi",
        fr_BE: "Prix",
        nl_BE: "Prijs",
        de_DE: "Preis",
        es_ES: "Precios",
      },
      enable_details: false,
      enable_main: true,
    });
  }

  if (
    attributes.value &&
    attributes.value?.pricing &&
    attributes.value?.pricing?.length
  ) {
    priceAttributes.value.push(...attributes.value.pricing);
  }
  return true;
  // }, 3000);
};
const getSeoDescription = (seoTitle) => {
  // Clean HTML tags once
  let clean = product.value.description
    ? product.value.description.replace(/<[^>]*>?/gm, "").replace(/\s+/g, " ").trim()
    : "";

  // const fallback = `${seoTitle} - ${t("seo.product_details_update_soon")}`;

  // Base description
  // let desc = clean
  //   ? clean.substring(0, 160) + "..."
  //   : fallback;

  // Funko override (brand 395)
  // if (product.value?.brand === 395) {
  const mpnSeo = product.value?.mpn ? ` MPN: ${product.value?.mpn}` : "";
  clean = clean
    ? `${seoTitle} - ${clean.substring(0, 160)}...`
    : `${seoTitle}${mpnSeo} - ${t("seo.product_details_update_soon")}`;
  // }

  return clean;
};
onBeforeUnmount(() => {
  productStore.setProductDetails({})
})
// Product structured data. No offers block on purpose: this is a B2B catalog
// where the real price is per-customer and behind login, and dvdSuggestedPriceAmount
// is an RRP, not what we sell at — advertising it as an Offer would misrepresent.
// The identifiers (gtin13/mpn/sku/brand) are what lets Google match the page to a
// known product entity, which is the indexing win.
const buildProductJsonLd = (title, desc) => {
  const p = product.value;
  const brand =
    typeof p.brand_text === "string"
      ? p.brand_text
      : p.brand_text?.[selectedLanguage.value] || p.brand_text?.en_GB || undefined;
  const ld = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: title,
    sku: p.identifier,
    description: desc,
    image: (p.images || []).slice(0, 5).map((img) => getImgUrl(img, 900)),
    url: `https://cld.eu/products/${id}/${productLinkTitleString.value(title)}`,
  };
  if (p.ean) ld.gtin13 = String(p.ean);
  if (p.mpn) ld.mpn = String(p.mpn);
  if (brand) ld.brand = { "@type": "Brand", name: brand };
  if (p.countryOfOrigin) ld.countryOfOrigin = p.countryOfOrigin;
  return JSON.stringify(ld);
};

watchEffect(() => {
  if (product.value && product.value?.identifier) {
    // computed_attr concatenates attribute strings and can carry double spaces —
    // visible in the SERP title, so collapse them.
    const title = (product.value.computed_attr || "").replace(/\s+/g, " ").trim();
    const ogImg = getImgUrl(product.value.images[slideId.value], 450)
    const desc = getSeoDescription(title);
    useHead({
      title: title,
      script: [
        {
          key: "product-jsonld",
          type: "application/ld+json",
          innerHTML: buildProductJsonLd(title, desc),
        },
      ],
      link: [
        {
          rel: "preload",
          as: "image",
          href: `https://assets.cld.eu/cdn-cgi/image/format=webp,width=600,height=900,quality=80/${product.value.images[0]}`,
          fetchpriority: "high",
        },
      ],
      meta: [
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:url',
          content: `https://cld.eu/products/${id}/${productLinkTitleString.value(title)}`,
        },
        {
          property: 'og:description',
          content: desc,
        },
        {
          name: 'description',
          content: desc,
        },
        {
          property: 'og:image',
          content: ogImg
        },

      ],
    })
  }
  else {
    // useHead({
    //   title: product.value.name[selectedLanguage.value],
    //   meta: [
    //     {
    //       property: 'og:title',
    //       content: product.value.name[selectedLanguage.value],
    //     },
    //     {
    //       property: 'og:url',
    //       content: `https://cld.eu/products/${id}/${name}`,
    //     },
    //     {
    //       property: 'og:description',
    //       content: name.replaceAll("-", " ")
    //     },
    //     {
    //       name: 'description',
    //       content: name.replaceAll("-", " "),
    //     },
    //   ],
    // })
  }
})

const activeTab = ref(
  product.value?.description?.length ? "top-home" : "top-profile"
);


const isSwiperReady = ref(false);
onMounted(() => {
  setTimeout(() => {
    isSwiperReady.value = true;
  }, 100);
});

definePageMeta({
  // layout:'',
  middleware: 'set-layout'
});
</script>

<template>
  <div>
    <section v-if="product && product.name" class="py-8">
      <div class="mx-auto w-full max-w-7xl px-4">
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div class="lg:col-span-12">
            <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <div class="lg:col-span-7">
                <div class=" min-h-[460px]">
                  <div class="relative">
                    <span
                      v-if="product.vg_menu_languages?.length"
                      class="flagicon-container absolute left-3 top-3 z-10"
                      v-html="product.vg_menu_languages"
                    ></span>

                    <!-- Static LCP Image Container (SSR + First Paint) -->
                    <div v-if="!isSwiperReady" class="w-full rounded-2xl bg-white mb-4 border border-gray-100 overflow-hidden">
                      <div class="p-2">
                         <img
                           v-if="product.images && product.images[0]"
                           :src="`https://assets.cld.eu/cdn-cgi/image/format=webp,width=600,height=900,quality=80/${product.images[0]}`"
                           class="w-full h-[460px] sm:h-[560px] object-contain"
                           :alt="product.name"
                           width="900"
                           height="900"
                           loading="eager"
                           fetchpriority="high"
                         />
                         <div v-else class="h-[460px] w-full animate-pulse rounded-2xl bg-gray-100"></div>
                      </div>
                    </div>
                    
                    <!-- Interactive Swiper (Hydrated later) -->
                    <div v-else>
                       <ProductInfoImageSwiper :images="product.images" :name="product.name" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="rtl-text lg:col-span-5">
                <div class="px-0 lg:px-4">
                  <!-- Header -->
                  <div class="border-b border-gray-100 pb-6 mb-6">
                    <h1 v-if="product.name" class="text-3xl font-bold text-gray-900 mb-2 leading-tight">
                      {{ product.name }}
                    </h1>

                    <div class="flex flex-wrap gap-4 text-sm text-gray-500 items-center">
                      <span v-if="product.identifier" class="flex items-center gap-1">
                        ID: <span class="font-medium text-gray-900">{{ product.identifier }}</span>
                      </span>
                      <span v-if="product.ean" class="hidden sm:inline text-gray-300">|</span>
                      <span v-if="product.ean" class="flex items-center gap-1">
                        EAN: <span class="font-medium text-gray-900">{{ product.ean }}</span>
                      </span>
                      <span v-if="product.refCli" class="hidden sm:inline text-gray-300">|</span>
                      <span v-if="product.refCli" class="flex items-center gap-1">
                        RefCli: <span class="font-medium text-gray-900">{{ product.refCli }}</span>
                      </span>
                    </div>

                    <!-- Publisher / license page links: show for every product
                         with a brand / license (CMS slug when curated,
                         slugified facet value otherwise) -->
                    <div class="flex flex-wrap gap-x-6 gap-y-1">
                      <NuxtLink
                        v-if="publisherPageLink"
                        :to="publisherPageLink.href"
                        class="group inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[var(--color-primary)] hover:text-[#008556]"
                      >
                        {{ $t('publisher.see_publisher', { name: publisherPageLink.name }) }}
                        <span class="transition-transform group-hover:translate-x-1" aria-hidden="true">→</span>
                      </NuxtLink>
                      <NuxtLink
                        v-if="licensePageLink"
                        :to="licensePageLink.href"
                        class="group inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-[var(--color-primary)] hover:text-[#008556]"
                      >
                        {{ $t('license.see_license', { name: licensePageLink.name }) }}
                        <span class="transition-transform group-hover:translate-x-1" aria-hidden="true">→</span>
                      </NuxtLink>
                    </div>
                  </div>

                  <!-- Price & Main Info -->
                  <ClientOnly>
                    <div class="mb-8">
                      <!-- Lazy: this gauge is a DevExtreme circular-gauge, and only a
                           small minority of products are in presale. Non-lazy it sat in
                           the product page chunk for every product. -->
                      <div v-if="preSalePercentage && preSalePercentage > 0" class="mb-4">
                        <LazyWidgetsProgress :percentage="preSalePercentage" />
                      </div>

                      <div  class="flex flex-col">
                        <template v-if="product.price">
                        <span class="text-sm text-gray-500 mb-1">{{ $t('price') }}</span>
                        <h3 class="text-4xl font-bold text-primary">€ {{ showDecimalValueInLocale(product.price) }}</h3>
                        </template>
                        <p v-if="product.dvdSuggestedPriceAmount" class="text-sm text-gray-500 mt-1">
                          {{ $t('products.retail_price') }}: € {{
                            showDecimalValueInLocale(product.dvdSuggestedPriceAmount) }}
                        </p>
                      </div>
                    </div>
                  </ClientOnly>

                  <!-- Stock & Actions Container -->
                  <div class="mt-[10px] bg-white mb-8 overflow-hidden">

                    <!-- Actions -->
                    <template v-if="isAuthenticated && !product.readOnly">
                      <WidgetsLoader :loading="isLoading">
                        <div class="p-6">
                          <div class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">{{
                            $t('datagrid.quantity') }}</div>
                          <div class="flex flex-col sm:flex-row gap-4 h-[48px]">
                            <!-- Add to Cart (large button) -->
                            <div class="w-full sm:w-35 h-full">
                              <button @click="addToCart"
                                :disabled="isLoading"
                                class="w-full h-full !bg-[#4CAF50] hover:!bg-[#43A047] text-white font-bold rounded-lg shadow-sm hover:shadow-md transition-colors flex items-center justify-center gap-3 uppercase text-sm tracking-wider disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:!bg-[#4CAF50] disabled:hover:shadow-sm">
                                <i class="ti-shopping-cart text-lg"></i>
                                {{ $t('products.add_to_cart') }}
                              </button>
                            </div>
                            <!-- Quantity Input -->
                            <div class="w-full sm:w-35 h-full">
                              <div
                                class="flex items-center h-full w-full border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                                <button type="button" @click="decrement"
                                  class="w-12 h-full flex items-center justify-center text-gray-400 hover:text-green-600 hover:bg-gray-50 border-r-2 border-gray-200 transition-colors cursor-pointer disabled:opacity-30 disabled:hover:bg-white"
                                  :disabled="selectedQuantity <= 1" aria-label="Decrease quantity">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                  </svg>
                                </button>
                                <input :value="selectedQuantity" @input="onQuantityInput" @blur="onQuantityBlur"
                                  type="text" inputmode="numeric" maxlength="5"
                                  class="flex-1 min-w-0 h-full text-center border-none p-0 text-gray-900 font-bold focus:ring-0 bg-transparent text-xl"
                                  aria-label="Quantity" />
                                <button type="button" @click="increment"
                                  class="w-12 h-full flex items-center justify-center text-gray-400 hover:text-green-600 hover:bg-gray-50 border-l-2 border-gray-200 transition-colors cursor-pointer"
                                  aria-label="Increase quantity">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                                    fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                                    stroke-linejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                  </svg>
                                </button>
                              </div>
                            </div>

                          </div>
                        </div>
                      </WidgetsLoader>

                      <!-- Images Button (Full width, bordered top) -->
                  <div class="mt-2 border-t-2 border-x-0 border-b-0 border-gray-100 bg-gray-50/50">
                        <button v-if="!isDownloading" @click="downloadHighResImages"
                          class="w-full py-4 text-sm font-bold text-gray-600 hover:text-[#4CAF50] hover:bg-white transition-all flex items-center justify-center gap-2 group cursor-pointer uppercase tracking-wide">
                          <span>{{ $t('images') }}</span>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            class="text-gray-400 group-hover:text-[#4CAF50] transition-colors">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7 10 12 15 17 10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                          </svg>
                        </button>
                        <div v-else class="w-full py-4 flex items-center justify-center">
                          <i class="fa fa-spinner fa-spin text-xl text-[rgb(3,183,120)]"></i>
                        </div>
                      </div>
                    </template>

                    <!-- Variants for Not Logged In / Read Only -->
                    <template v-else-if="!product.readOnly">
                      <div class="p-6 text-center sm:text-left">
                        <div
                          class="flex items-center justify-center sm:justify-start gap-2 mb-2 font-semibold text-gray-900">
                          <i class="fa fa-lock text-gray-400"></i>
                          <span>{{ $t('see_wholesale_price') }}</span>
                        </div>
                        <div class="flex gap-3 justify-center sm:justify-start pt-2">
                          <NuxtLinkLocale to="/account/pre-signup" class="cld-btn cld-btn-primary btn-sm">{{
                            $t('login_p.create_account') }}</NuxtLinkLocale>
                          <NuxtLinkLocale :to="{ path: `/account/login`, query: { redirectTo: route.fullPath } }"
                            rel="nofollow" class="cld-btn cld-btn-outline btn-sm">{{ $t('login_p.login') }}</NuxtLinkLocale>
                        </div>
                      </div>
                    </template>

                    <template v-else-if="product.readOnly && isAuthenticated">
                      <div class="p-4 text-center">
                        <span class="font-medium text-gray-900">{{ $t('not_available_for_purchase') }}</span>
                      </div>
                      <div class="border-t border-gray-100">
                        <button v-if="!isDownloading" @click="downloadHighResImages"
                          class="w-full py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors">
                          {{ $t('images') }}
                        </button>
                        <div v-else class="w-full py-3 flex items-center justify-center">
                          <i class="fa fa-spinner fa-spin text-xl text-[rgb(3,183,120)]"></i>
                        </div>
                      </div>
                    </template>
                  </div>

                  <!-- Stock Status (Moved out of the card to sit above or below it, or keep inside if part of the card logic? 
                         In reference, stock is usually separate. I will place it just above the card for better hierarchy) -->
                  <div class="mb-4" v-if="!product.statusPreorder && isAuthenticated">
                    <div v-if="product.stock !== 0 && product.stock < 8"
                      class="inline-flex items-center gap-2 text-red-600 text-sm font-medium">
                      <i class="far fa-clock"></i>
                      {{ $t('hurry_stock', { stock: product.stock }) }}
                    </div>
                    <div v-else class="inline-flex items-center gap-2 text-green-600 text-sm font-medium">
                      <span class="w-2 h-2 bg-green-500 rounded-full"></span>
                      <span>{{ $t('stock') }} {{ product.stock }}</span>
                    </div>
                  </div>

                  <!-- Attributes Info -->
                  <div class="space-y-4">
                    <div v-if="backOrders && backOrders[`${product.identifier}`]"
                      class="text-sm text-orange-600 bg-orange-50 px-4 py-3 rounded-lg border border-orange-100 flex items-start gap-2">
                      <i class="fa fa-info-circle mt-0.5"></i>
                      <div>
                        <strong>{{ $t('products.back_order') }}:</strong> {{ backOrders[`${product.identifier}`] }}
                      </div>
                    </div>

                    <div v-for="(value, i) in priceAttributes" :key="i">
                      <div
                        v-if="value.enable_main && value.type === 'pim_catalog_price_collection' && value.data"
                        class="flex justify-between items-center py-2 border-b border-gray-100 text-sm">
                        <span class="text-gray-600">{{ value?.options?.i18nKey ? $t(value.options.i18nKey) : getText(value.options) }}</span>
                        <span class="font-medium text-gray-900">€ {{ showDecimalValueInLocale(value.data) }}</span>
                      </div>
                    </div>

                    <template v-if="attributesMainCatalogText?.length">
                      <div class="pt-2">
                        <div v-for="(attGroup, i) of attributesMainCatalogText" :key="i"
                          class="border-b border-gray-100 last:border-0 pb-2">
                          <h5 class="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide"
                            v-if="i === 0 && attGroup.groupLables?.[selectedLanguage]">
                            {{ attGroup.groupLables?.[selectedLanguage] }}
                          </h5>
                          <div v-if="attGroup.options[selectedLanguage]" class="flex justify-between text-sm py-1">
                            <span class="text-gray-600">{{ getText(attGroup.options) }}</span>
                            <span class="font-medium text-gray-900 text-right">{{ attGroup.data }}</span>
                          </div>
                        </div>
                      </div>
                    </template>

                    <ShowMorePanel align="start" class="border-t border-gray-100 pt-4 mt-4">
                      <div class="space-y-6">
                        <template v-for="(attGroups, i) of attributesMain" :key="`${i}-attr0-main`">
                          <div v-if="Array.isArray(attGroups) && attGroups.length">
                            <h5 class="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wide">
                              {{ attGroups?.[0]?.groupLables?.[selectedLanguage] || attGroups?.[0]?.groupLables?.en_GB
                              }}
                            </h5>
                            <div class="space-y-2">
                              <div v-for="(attGroup, j) in attGroups" :key="`${i}-${j}-attrGroup`"
                                class="text-sm border-b border-gray-50 last:border-0 pb-2 mb-2">
                                <!-- Content Logic Same as Before -->
                                <template v-if="attGroup.type === 'pim_catalog_multiselect'">
                                  <div class="flex flex-col gap-2">
                                    <span class="text-gray-600">{{ getText(attGroup.options) }}</span>
                                    <div class="flex flex-wrap gap-1">
                                      <template v-for="multiOption in attGroup.data"
                                        :key="multiOption?.Code || JSON.stringify(multiOption)">
                                        <ProductInfoPegiDescriptorBadge v-if="isPegiDescriptors(attGroup)"
                                          :option="multiOption" :language="selectedLanguage" />
                                        <span v-else
                                          class="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs border border-gray-200">
                                          {{ getText(multiOption.Labels) }}
                                        </span>
                                      </template>
                                    </div>
                                  </div>
                                </template>

                                <template
                                  v-else-if="attGroup.type === 'pim_catalog_simpleselect' && attGroup.options && attGroup.options[selectedLanguage]">
                                  <div class="flex justify-between items-center">
                                    <span class="text-gray-600">{{ getText(attGroup.options) }}</span>
                                    <ProductInfoRatingBadge v-if="isRatingAttr(attGroup)"
                                      :label="attGroup.options?.en_GB || ''" :value="getText(attGroup.data)" />
                                    <!-- Brand value links to the publisher page -->
                                    <NuxtLink v-else-if="isBrandAttr(attGroup) && publisherPageLink"
                                      :to="publisherPageLink.href" :title="$t('publisher.see_publisher', { name: publisherPageLink.name })"
                                      class="font-medium text-[var(--color-primary)] hover:text-[#008556] hover:underline inline-flex items-center gap-1">
                                      {{ getText(attGroup.data) }}
                                      <span aria-hidden="true">→</span>
                                    </NuxtLink>
                                    <span v-else class="font-medium text-gray-900">{{ getText(attGroup.data) }}</span>
                                  </div>
                                </template>

                                <template
                                  v-else-if="attGroup.type === 'pim_catalog_textarea' && attGroup.options[selectedLanguage]">
                                  <div class="flex flex-col gap-1">
                                    <span class="text-gray-600">{{ getText(attGroup.data["lables"]) }}</span>
                                    <span class="font-medium text-gray-900">{{ attGroup.data }}</span>
                                  </div>
                                </template>

                                <template v-else-if="attGroup.type === 'pim_catalog_boolean' && attGroup.data">
                                  <div class="flex justify-between">
                                    <span class="text-gray-600">{{ getText(attGroup.options) }}</span>
                                    <span class="font-medium text-gray-900">{{ attGroup.data ? $t('yes') : $t('no')
                                      }}</span>
                                  </div>
                                </template>

                                <template
                                  v-else-if="attGroup.type === 'pim_catalog_date' && attGroup.options[selectedLanguage]">
                                  <div class="flex justify-between">
                                    <span class="text-gray-600">{{ getText(attGroup.options) }}</span>
                                    <span class="font-medium text-gray-900">{{ new
                                      Date(attGroup.data).toLocaleDateString(selectedLanguage.replace("_", "-"))
                                      }}</span>
                                  </div>
                                </template>

                                <template
                                  v-else-if="(attGroup.type === 'pim_catalog_metric' || attGroup.type === 'pim_catalog_number') && attGroup.options[selectedLanguage]">
                                  <div class="flex justify-between">
                                    <span class="text-gray-600">{{ getText(attGroup.options) }}</span>
                                    <span class="font-medium text-gray-900">{{ attGroup.data.toString() }}</span>
                                  </div>
                                </template>
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </ShowMorePanel>

                    <div v-if="product?.sales_multiple"
                      class="flex justify-between items-center py-2 text-sm border-t border-gray-100">
                      <span class="text-gray-600">{{ $t('products.sale_in_multiple') }}</span>
                      <span class="font-medium text-gray-900">{{ product.sales_multiple }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Details & Specs Tabs -->
            <div class="mt-10">
              <div class="border-b border-gray-200">
                <nav class="-mb-px flex gap-8" aria-label="Tabs">
                  <button 
                    v-if="product.description && product.description.length" 
                    @click="activeTab = 'top-home'"
                    class="pb-4 px-1 border-b-2 text-sm font-bold uppercase tracking-wider transition-colors duration-200 ease-in-out focus:outline-none"
                    :class="activeTab === 'top-home'
                      ? 'border-[#4CAF50] text-[#4CAF50]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                  >
                    {{ $t('details') }}
                  </button>

                  <button 
                    @click="activeTab = 'top-profile'"
                    class="pb-4 px-1 border-b-2 text-sm font-bold uppercase tracking-wider transition-colors duration-200 ease-in-out focus:outline-none"
                    :class="activeTab === 'top-profile'
                      ? 'border-[#4CAF50] text-[#4CAF50]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                  >
                    {{ $t('specification') }}
                  </button>
                </nav>
              </div>

              <div class="py-6">
                <!-- Content -->
                <div v-show="activeTab === 'top-home'" class="animate-fadeIn">
                   <div v-if="product.description && product.description.length" class="prose max-w-none text-gray-600 leading-relaxed" v-html="product.description"></div>
                </div>

                <div v-show="activeTab === 'top-profile'" class="animate-fadeIn">
                   <LazyProductAttributes :attributes-json="attributesDetails" :selected-language="selectedLanguage" />
                </div>
              </div>
            </div>

            <!-- Recently Viewed Products -->
            <div class="mt-12 pt-8 border-t border-gray-200">
              <div v-if="recentlyViewed?.length">
                <ProductsSideBar layout="grid" :title="$t('recently_viewed')" :products="recentlyViewed"></ProductsSideBar>
              </div>
            </div>
          </div>

          <aside class="lg:col-span-3 lg:sticky lg:top-24">
            <div class="max-h-[700px] overflow-y-auto overflow-x-hidden">
              <div v-if="associationsProducts.length">
                <ProductsSideBar title="Bought Together" :products="associationsProducts"></ProductsSideBar>
              </div>
            </div>
          </aside>
        </div>
      </div>
      <!-- <relatedProduct :productTYpe="productTYpe" :productId="productId" /> -->
    </section>
    <ClientOnly>
      <div class="container " v-if="!product?.name && status === 'pending'">
        <div class="text-center">
          <LazyWidgetsLoader class="loader-center" :loading="true"> </LazyWidgetsLoader>
        </div>
      </div>

      <div class="container section-b-space " v-if="status !== 'pending' && (!product || !product.identifier)">
        <div class="theme-card card-md text-center">
          <div class="col-md">
            <h3 class="text-red text-uppercase"> {{ $t("products.no_product") }} </h3>
            <NuxtLinkLocale to="/products/search" class=" btn btn-outline btn-sm">
              {{ "Go" }} {{ $t("routes.products-search") }} </NuxtLinkLocale>
          </div>
        </div>
      </div>
    </ClientOnly>

  </div>
</template>

<style></style>